import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import type { ICsrfTokenResponse } from './interfaces/auth.interface';
import { map, mergeMap } from 'rxjs';
import { urlFactory } from './utils/url';

@Injectable({
  providedIn: 'root',
})
export class BaseApiService {
  readonly #http = inject(HttpClient);
  readonly #toUrl = urlFactory(environment.baseApiUrl);

  getCsrfToken() {
    const url = this.#toUrl('/csrf-token');
    return this.#http.get<ICsrfTokenResponse>(url, { withCredentials: true });
  }

  get<T>(path: string, params = {}) {
    const url = this.#toUrl(path);
    return this.#http.get<T>(url, { params: params, withCredentials: true });
  }

  post<T>(path: string, body: unknown) {
    const url = this.#toUrl(path);
    return this.getCsrfToken().pipe(
      mergeMap((res) => {
        const csrfToken = res.csrfToken;
        const httpHeaders = new HttpHeaders({
          'X-CSRFToken': csrfToken,
          Accept: 'application/json',
        });
        return this.#http.post<T>(url, body, { headers: httpHeaders, withCredentials: true });
      }),
    );
  }

  uploadImageWithProgress<T>(path: string, body: unknown) {
    const url = this.#toUrl(path);
    return this.getCsrfToken().pipe(
      mergeMap((res) => {
        const csrfToken = res.csrfToken;
        const httpHeaders = new HttpHeaders({
          'X-CSRFToken': csrfToken,
          Accept: 'application/json',
        });
        return this.#http.post<T>(url, body, { headers: httpHeaders, withCredentials: true, reportProgress: true, observe: 'events' }).pipe(
          map((event) => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                // アップロードプログレスイベントを処理
                return { progress: Math.round((100 * event.loaded) / (event.total ?? 1)) };
              case HttpEventType.Response:
                // レスポンスイベントを処理
                return { progress: 100, body: event.body };
              default:
                // その他のイベントは無視
                return { progress: 0 };
            }
          }),
        );
      }),
    );
  }

  put<T>(path: string, body: unknown) {
    const url = this.#toUrl(path);
    return this.getCsrfToken().pipe(
      mergeMap((res) => {
        const csrfToken = res.csrfToken;
        const httpHeaders = new HttpHeaders({
          'X-CSRFToken': csrfToken,
          Accept: 'application/json',
        });
        return this.#http.put<T>(url, body, { headers: httpHeaders, withCredentials: true });
      }),
    );
  }

  delete<T>(path: string) {
    const url = this.#toUrl(path);
    return this.getCsrfToken().pipe(
      mergeMap((res) => {
        const csrfToken = res.csrfToken;
        const httpHeaders = new HttpHeaders({
          'X-CSRFToken': csrfToken,
          Accept: 'application/json',
        });
        return this.#http.delete<T>(url, { headers: httpHeaders, withCredentials: true });
      }),
    );
  }
}
